import { PriceFormatter } from '@estee/elc-formatters';
import Config from '../domain/entities/Config/Config';

const LOCALES = {
  BE_LOCALE: 'nl-BE',
  CA_LOCALE_SUFFIX: '-CA',
  CL_LOCALE: 'es-CL',
  CZ_LOCALE: 'cs-CZ',
  DK_LOCALE: 'da-DK',
  FI_LOCALE: 'fi-FI',
  HK_LOCALE_EN: 'en-HK',
  HK_LOCALE_ZH: 'zh-HK',
  HU_LOCALE: 'hu-HU',
  MY_LOCALE: 'en-MY',
  NO_LOCALE: 'no-NO',
  PL_EN_LANG_LOCALE: 'en-PL',
  PL_PL_LANG_LOCALE: 'pl-PL',
  SE_LOCALE: 'sv-SE',
  TR_LOCALE: 'tr-TR',
  TW_LOCALE: 'zh-TW',
  US_LOCALE_SUFFIX: '-US',
  VN_LOCALE: 'vi-VN',
} as const;

type Locale = typeof LOCALES[keyof typeof LOCALES];

const {
  CA_LOCALE_SUFFIX,
  US_LOCALE_SUFFIX,
  PL_EN_LANG_LOCALE,
  PL_PL_LANG_LOCALE,
  TR_LOCALE,
  MY_LOCALE,
  VN_LOCALE,
  CL_LOCALE,
  DK_LOCALE,
  NO_LOCALE,
  BE_LOCALE,
  SE_LOCALE,
  CZ_LOCALE,
  FI_LOCALE,
  HU_LOCALE,
  HK_LOCALE_EN,
  HK_LOCALE_ZH,
  TW_LOCALE,
} = LOCALES;

const DECIMAL_SEPARATOR = '.';
const space = ' ';

const REVERSE_CURRENCY_SYMBOL_LOCALES: Locale[] = [BE_LOCALE, PL_EN_LANG_LOCALE, PL_PL_LANG_LOCALE, HU_LOCALE];
const REPLACE_DECIMAL_WITH_DOT_LOCALES: Locale[] = [CZ_LOCALE, NO_LOCALE, PL_EN_LANG_LOCALE, PL_PL_LANG_LOCALE, HU_LOCALE];
const REPLACE_SEPARATOR_LOCALES: Locale[] = [PL_EN_LANG_LOCALE, BE_LOCALE];

const adjustLocale = (locale: Locale): Locale => {  
  if (locale.endsWith(CA_LOCALE_SUFFIX)) {
    return locale.replace(CA_LOCALE_SUFFIX, US_LOCALE_SUFFIX) as Locale;
  }
  if (locale === PL_EN_LANG_LOCALE) {
    return PL_PL_LANG_LOCALE;
  }
  return locale;
};

function addThousandsSeparator(value: string, separator: string): string {
  const formatted = value.split('').reverse().join('')
    .replace(/(\d{3}(?!.*\.|$))/g, `$1${separator}`).split('').reverse().join('');
  
  // replace extra delimiter added
  const cleanExtraSeparatorRegex = new RegExp(String.raw`^(\D*?)()${separator}`, 'g');

  return formatted.replace(cleanExtraSeparatorRegex, '$1').replace(/ /g, '');
}

export default function formatPrice(config: Config, price: number): string {
  const { currency, locale } = config as { currency: string; locale: Locale };
  const decimalSeparator = REPLACE_DECIMAL_WITH_DOT_LOCALES.includes(locale) ? DECIMAL_SEPARATOR : '';
  const numberOfDecimals = locale === VN_LOCALE && 2;

  const priceFormatterConfig = {
    currency,
    decimalSeparator,
    locale: adjustLocale(locale),
    ...(numberOfDecimals && { numberOfDecimals }),
  };
  
  const priceFormatter = new PriceFormatter(priceFormatterConfig);
  const originalFormattedPrice = priceFormatter.formatPriceWithCurrency(price);
  let finalFormattedPrice = originalFormattedPrice;

  if (locale === TR_LOCALE) {
    const turkishPrice = originalFormattedPrice.replace(/\./g, '').replace(/,/g, '.').replace('₺', 'TL');
    finalFormattedPrice = price >= 0
      ? turkishPrice.slice(2) + space + turkishPrice.slice(0, 2)
      : turkishPrice.slice(0, 1) + turkishPrice.slice(3) + space + turkishPrice.slice(1, 3);
  }

  if (locale === TW_LOCALE) {
    finalFormattedPrice = originalFormattedPrice
      .replace(/\$/g, (str: string) => (originalFormattedPrice.includes('NT$') ? str : 'NT$'))
      .replace(/\.00/g, '');
 
  }

  if (locale === MY_LOCALE) {
    finalFormattedPrice = originalFormattedPrice.replace(/\s/g, '');
  }

  if (locale === VN_LOCALE) {
    finalFormattedPrice = originalFormattedPrice.replace(/\D00(?=\D*$)/, '');
  }

  if (locale === CL_LOCALE) {
    finalFormattedPrice = originalFormattedPrice.replace(/^(\$)?-([\d,.]+)/, '-$1$2');
  }

  if (locale === NO_LOCALE) {
    finalFormattedPrice = originalFormattedPrice
      .replace(/(\d)\s(?=\d)/g, '$1,')
      .replace(/−/g, '-')
      .replace(/kr/g, 'KR');
    
    const currencyAtEnd = finalFormattedPrice.match(/^([-\d,.].*?)\s*([^\d\s,.-]+)$/);
    /* istanbul ignore else*/
    if (currencyAtEnd) {
      const value = currencyAtEnd[1].trim();
      const currencySymbol = currencyAtEnd[2].trim();
      finalFormattedPrice = `${currencySymbol}${value}`;
    }
  }

  if (locale === SE_LOCALE) {
    finalFormattedPrice = originalFormattedPrice
      .replace(/(\d)\s+(?=\d)/g, '$1.')
      .replace(/−/g, '-');
    
    finalFormattedPrice = finalFormattedPrice.replace(/\s+([^\d\s]+)$/, '$1');
  }

  if (locale === PL_PL_LANG_LOCALE) {
    finalFormattedPrice = originalFormattedPrice
      .replace(/(\d)\s+(?=\d)/g, '$1')
      .replace(/\s+([^\d\s]+)$/, '$1');
  }

  if (locale === FI_LOCALE) {
    finalFormattedPrice = originalFormattedPrice
      .replace(/(\d)\s+(?=\d)/g, '$1.')
      .replace(/\s+€$/, '€')
      .replace(/−/g, '-');
  }

  if (locale === HU_LOCALE) {
    finalFormattedPrice = originalFormattedPrice
      .replace(/(\d)\s+(?=\d)/g, '$1,')
      .replace(/\s+([^\d\s]+)$/, '$1')
      .replace(/−/g, '-');
    
    finalFormattedPrice = finalFormattedPrice.replace(/\b(\d+)([,.])/g, (match, digits, separator) => {
      const formattedDigits = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return formattedDigits + separator;
    });
  }

  if (locale === DK_LOCALE) {
    finalFormattedPrice = originalFormattedPrice
      .replace(/^([^\d\s]+)\.\s+/, '$1.');
    
    const currencyAtEnd = finalFormattedPrice.match(/^([-\d,.].*?)\s*([^\d\s,.-]+)\.$/);
    /* istanbul ignore else*/
    if (currencyAtEnd) {
      const value = currencyAtEnd[1].trim();
      const currencySymbol = currencyAtEnd[2].trim();
      finalFormattedPrice = `${currencySymbol}.${value}`;
    }
  }

  if (REVERSE_CURRENCY_SYMBOL_LOCALES.includes(locale)) {
    const currencyAtStart = finalFormattedPrice.match(/^([^\d-−]+)([-−\d].*)$/);
    if (currencyAtStart) {
      const currencySymbol = currencyAtStart[1].trim();
      const value = currencyAtStart[2].trim();
      finalFormattedPrice = `${value} ${currencySymbol}`;
    } else {
      const currencyAtEnd = finalFormattedPrice.match(/^([-−\d].*?)\s*([^\d-−]+)$/);
      /* istanbul ignore else*/
      if (currencyAtEnd) {
        const value = currencyAtEnd[1].trim();
        const currencySymbol = currencyAtEnd[2].trim();
        finalFormattedPrice = `${currencySymbol} ${value}`;
      }
    }
  }

  if (locale === PL_PL_LANG_LOCALE || locale === PL_EN_LANG_LOCALE) {
    finalFormattedPrice = addThousandsSeparator(finalFormattedPrice, ',');
  }

  if (locale === HK_LOCALE_EN || locale === HK_LOCALE_ZH) {
    finalFormattedPrice = originalFormattedPrice
      .replace(/(\.00)$/, '')
      .replace(/(\.[1-9])0+/g, '$1');
  }

  if (REPLACE_SEPARATOR_LOCALES.includes(locale)) {
    finalFormattedPrice = finalFormattedPrice
      .replace(/^(\D+)\s+(\d)/, '$1$2')
      .replace(/(\d)\s+(\D+)$/, '$1$2');
  }

  return finalFormattedPrice;
}
